import { useState, useEffect, useCallback } from 'react'
import { Table, Card, Button, Input, Select, Space, Modal, Form, message, Popconfirm, Tag, InputNumber, Descriptions, Divider, Row, Col, Alert } from 'antd'
import { PlusOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined } from '@ant-design/icons'
import { productionApi, bomApi, productApi, inventoryApi } from '../services/api'

interface MaterialItem {
  id?: number
  material_id: number
  material_type: number
  batch_no?: string
  actual_qty: number
  unit: string
  cost_price: number
  material?: { id: number; name: string; code: string; unit: string }
}

interface ProductionPlan {
  id: number
  plan_no: string
  product_id: number
  status: number
  actual_output: number
  waste_rate: number
  total_cost: number
  unit_cost: number
  remark: string
  started_at: string
  completed_at: string
  created_at: string
  warehouse_id?: number
  product?: { id: number; name: string; code: string; main_image: string; unit: string }
  warehouse?: { id: number; name: string }
  materials?: MaterialItem[]
}

const statusMap: Record<number, { color: string; text: string }> = {
  0: { color: 'orange', text: '待确认' },
  1: { color: 'blue', text: '生产中' },
  2: { color: 'green', text: '已完成' },
  3: { color: 'default', text: '已取消' }
}

const ProductionPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<ProductionPlan[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState<number | undefined>(undefined)

  const [createVisible, setCreateVisible] = useState(false)
  const [completeVisible, setCompleteVisible] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [currentPlan, setCurrentPlan] = useState<ProductionPlan | null>(null)

  // 通用数据
  const [products, setProducts] = useState<any[]>([])
  const [warehouses, setWarehouses] = useState<any[]>([])

  // 创建表单
  const [selectedProduct, setSelectedProduct] = useState<number | undefined>()
  const [selectedWarehouse, setSelectedWarehouse] = useState<number | undefined>()
  const [mainMaterials, setMainMaterials] = useState<any[]>([])

  // 完工表单
  const [completeOutput, setCompleteOutput] = useState<number>(0)
  const [completeWasteRate, setCompleteWasteRate] = useState<string>('')
  const [auxMaterials, setAuxMaterials] = useState<any[]>([])

  useEffect(() => { fetchPlans(); fetchProducts(); fetchWarehouses() }, [page, pageSize, status])

  const fetchPlans = async () => {
    setLoading(true)
    try {
      const res: any = await productionApi.getPlans({ page, page_size: pageSize, keyword, status })
      setData(res.data.list || [])
      setTotal(res.data.total || 0)
    } catch { message.error('获取生产单列表失败') }
    setLoading(false)
  }

  const fetchProducts = async () => {
    try {
      const res: any = await productApi.getProducts({ page: 1, page_size: 1000 })
      setProducts(res.data.list || [])
    } catch { console.error('获取商品列表失败') }
  }

  const fetchWarehouses = async () => {
    try {
      const res: any = await inventoryApi.getWarehouses()
      setWarehouses(res.data || [])
    } catch { console.error('获取仓库列表失败') }
  }

  // 加载BOM主料/辅料列表
  const loadBOMItems = useCallback(async (productId: number) => {
    try {
      const res: any = await bomApi.getBOMs({ page: 1, page_size: 100, status: 1 })
      const productBOM = (res.data.list || []).find((b: any) => b.product_id === productId)
      if (productBOM && productBOM.items) {
        const mains = productBOM.items.filter((i: any) => i.material_type === 1)
        const auxs = productBOM.items.filter((i: any) => i.material_type === 2)
        setMainMaterials(mains.map((m: any) => ({
          material_id: m.material_id,
          name: m.material?.name || m.product_name || '',
          code: m.material?.code || m.product_code || '',
          unit: m.material?.unit || m.unit || '',
          quantity: 0 // 人工填写
        })))
        setAuxMaterials(auxs.map((a: any) => ({
          material_id: a.material_id,
          name: a.material?.name || a.product_name || '',
          code: a.material?.code || a.product_code || '',
          unit: a.material?.unit || a.unit || '',
          quantity: 0
        })))
      } else {
        setMainMaterials([])
        setAuxMaterials([])
      }
    } catch { console.error('获取BOM失败') }
  }, [])

  // ===== 创建生产单 =====
  const handleCreate = () => {
    setSelectedProduct(undefined)
    setSelectedWarehouse(undefined)
    setMainMaterials([])
    setCreateVisible(true)
  }

  const handleProductChange = (productId: number) => {
    setSelectedProduct(productId)
    loadBOMItems(productId)
  }

  const handleMainQtyChange = (materialId: number, qty: number) => {
    setMainMaterials(prev => prev.map(m =>
      m.material_id === materialId ? { ...m, quantity: qty } : m
    ))
  }

  const handleSaveCreate = async () => {
    if (!selectedProduct) { message.error('请选择成品'); return }
    if (!selectedWarehouse) { message.error('请选择仓库'); return }
    const noQty = mainMaterials.find((m: any) => !m.quantity || m.quantity <= 0)
    if (noQty) { message.error(`请填写 ${noQty.name} 的投料量`); return }

    try {
      await productionApi.createPlan({
        product_id: selectedProduct,
        warehouse_id: selectedWarehouse,
        materials: mainMaterials.map((m: any) => ({
          material_id: m.material_id,
          quantity: m.quantity
        }))
      })
      message.success('生产单创建成功，主料已按先进先出自动投料')
      setCreateVisible(false)
      fetchPlans()
    } catch (err: any) {
      message.error(err.response?.data?.message || '创建失败')
    }
  }

  // ===== 完工入库 =====
  const handleComplete = (record: ProductionPlan) => {
    setCurrentPlan(record)
    setCompleteOutput(0)
    setCompleteWasteRate('')
    // 重新加载辅料列表
    if (record.product_id) loadBOMItems(record.product_id)
    setCompleteVisible(true)
  }

  const handleAuxQtyChange = (materialId: number, qty: number) => {
    setAuxMaterials(prev => prev.map(a =>
      a.material_id === materialId ? { ...a, quantity: qty } : a
    ))
  }

  const handleSaveComplete = async () => {
    if (!currentPlan) return
    if (!completeOutput || completeOutput <= 0) { message.error('请填入实际产出数量'); return }

    try {
      const payload: any = {
        actual_output: completeOutput,
        waste_rate: completeWasteRate !== '' ? parseFloat(completeWasteRate) : undefined,
        auxiliaries: auxMaterials.filter((a: any) => a.quantity && a.quantity > 0).map((a: any) => ({
          material_id: a.material_id,
          quantity: a.quantity
        }))
      }
      await productionApi.completePlan(currentPlan.id, payload)
      message.success('生产完成，成品已入库')
      setCompleteVisible(false)
      fetchPlans()
    } catch (err: any) {
      message.error(err.response?.data?.message || '完工失败')
    }
  }

  // ===== 取消 =====
  const handleCancel = async (id: number) => {
    try {
      await productionApi.cancelPlan(id)
      message.success('已取消，物料已退回')
      fetchPlans()
    } catch (err: any) {
      message.error(err.response?.data?.message || '取消失败')
    }
  }

  // ===== 详情 =====
  const handleViewDetail = async (record: ProductionPlan) => {
    try {
      const res: any = await productionApi.getPlan(record.id)
      setCurrentPlan(res.data)
      setDetailVisible(true)
    } catch { message.error('获取详情失败') }
  }

  // ===== 删除 =====
  const handleDelete = async (id: number) => {
    try {
      await productionApi.deletePlan(id)
      message.success('删除成功')
      fetchPlans()
    } catch (err: any) {
      message.error(err.response?.data?.message || '删除失败')
    }
  }

  const columns = [
    { title: '生产单号', dataIndex: 'plan_no', width: 160 },
    {
      title: '成品', dataIndex: 'product', width: 200,
      render: (product: any) => (
        <Space>
          {product?.main_image && <img src={product.main_image} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} />}
          <div>
            <div style={{ fontWeight: 500 }}>{product?.name}</div>
            <div style={{ color: '#999', fontSize: 12 }}>{product?.code}</div>
          </div>
        </Space>
      )
    },
    { title: '仓库', dataIndex: ['warehouse', 'name'], width: 100, render: (t: string) => t || '-' },
    {
      title: '产出', width: 120,
      render: (_: any, r: ProductionPlan) => r.status === 2 ? `${r.actual_output} ${r.product?.unit || ''}` : '-'
    },
    {
      title: '损耗率', width: 90,
      render: (_: any, r: ProductionPlan) => r.waste_rate != null ? `${r.waste_rate}%` : '-'
    },
    {
      title: '总成本', width: 110,
      render: (_: any, r: ProductionPlan) => r.total_cost != null ? `¥${Number(r.total_cost).toFixed(2)}` : '-'
    },
    { title: '状态', dataIndex: 'status', width: 90, render: (s: number) => <Tag color={statusMap[s]?.color || 'default'}>{statusMap[s]?.text || '未知'}</Tag> },
    {
      title: '操作', width: 200, fixed: 'right' as const,
      render: (_: any, record: ProductionPlan) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>详情</Button>
          {record.status === 1 && (
            <>
              <Button type="link" size="small" icon={<CheckCircleOutlined />} onClick={() => handleComplete(record)} style={{ color: '#52c41a' }}>完工</Button>
              <Popconfirm title="取消后物料将退回仓库，确定？" onConfirm={() => handleCancel(record.id)}>
                <Button type="link" size="small" danger icon={<CloseCircleOutlined />}>取消</Button>
              </Popconfirm>
            </>
          )}
          {record.status === 0 && (
            <>
              <Popconfirm title="确定取消？" onConfirm={() => handleCancel(record.id)}>
                <Button type="link" size="small" danger>取消</Button>
              </Popconfirm>
              <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
              </Popconfirm>
            </>
          )}
          {record.status === 3 && (
            <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ]

  // 详情里的主料/辅料分组
  const detailMains = currentPlan?.materials?.filter(m => m.material_type === 1) || []
  const detailAuxs = currentPlan?.materials?.filter(m => m.material_type === 2) || []

  return (
    <Card title="生产管理" extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>创建生产单</Button>}>
      <Space style={{ marginBottom: 16 }}>
        <Input placeholder="搜索成品名称/编码" value={keyword} onChange={e => setKeyword(e.target.value)} onPressEnter={() => { setPage(1); fetchPlans() }} style={{ width: 200 }} />
        <Select placeholder="状态" value={status} onChange={val => { setStatus(val); setPage(1) }} allowClear style={{ width: 120 }}
          options={Object.entries(statusMap).map(([k, v]) => ({ label: v.text, value: parseInt(k) }))} />
        <Button onClick={() => { setPage(1); fetchPlans() }}>搜索</Button>
      </Space>

      <Table columns={columns} dataSource={data} rowKey="id" loading={loading} scroll={{ x: 1100 }}
        pagination={{ current: page, pageSize, total, showSizeChanger: true, showTotal: t => `共 ${t} 条`, onChange: (p, ps) => { setPage(p); setPageSize(ps) } }} />

      {/* ===== 创建生产单弹窗 ===== */}
      <Modal title="创建生产单" open={createVisible} onOk={handleSaveCreate} onCancel={() => setCreateVisible(false)} width={700} destroyOnClose>
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="成品" required>
                <Select showSearch placeholder="选择成品" optionFilterProp="children" value={selectedProduct} onChange={handleProductChange} style={{ width: '100%' }}>
                  {products.map(p => <Select.Option key={p.id} value={p.id}>{p.name} ({p.code})</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="领料仓库" required>
                <Select placeholder="选择仓库" value={selectedWarehouse} onChange={setSelectedWarehouse} style={{ width: '100%' }}>
                  {warehouses.map(w => <Select.Option key={w.id} value={w.id}>{w.name}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {mainMaterials.length > 0 && (
          <>
            <Divider orientation="left">主料投料</Divider>
            <Alert message="系统将按先进先出原则自动从批次扣减库存" type="info" showIcon style={{ marginBottom: 12 }} />
            <Table size="small" dataSource={mainMaterials} rowKey="material_id" pagination={false}
              columns={[
                { title: '商品', dataIndex: 'name', width: 180 },
                { title: '编码', dataIndex: 'code', width: 120 },
                {
                  title: '投料量', width: 150,
                  render: (_: any, m: any) => (
                    <InputNumber min={0} value={m.quantity} onChange={v => handleMainQtyChange(m.material_id, v || 0)} style={{ width: '100%' }} placeholder="输入数量" />
                  )
                },
                { title: '单位', dataIndex: 'unit', width: 80 }
              ]}
            />
          </>
        )}
        {mainMaterials.length === 0 && selectedProduct && (
          <Alert message="该成品暂无BOM配方，请先创建配方" type="info" showIcon />
        )}
      </Modal>

      {/* ===== 完工入库弹窗 ===== */}
      <Modal title="完工入库" open={completeVisible} onOk={handleSaveComplete} onCancel={() => setCompleteVisible(false)} width={700} destroyOnClose>
        {currentPlan && (
          <>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="生产单号">{currentPlan.plan_no}</Descriptions.Item>
              <Descriptions.Item label="成品">{currentPlan.product?.name}</Descriptions.Item>
            </Descriptions>

            <Form layout="vertical">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="实际产出" required>
                    <InputNumber min={0} value={completeOutput} onChange={v => setCompleteOutput(v || 0)} style={{ width: '100%' }} placeholder="实际产出数量" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="损耗率(%)" tooltip="留空则自动计算">
                    <InputNumber min={0} max={100} value={completeWasteRate ? parseFloat(completeWasteRate) : undefined} onChange={v => setCompleteWasteRate(v != null ? String(v) : '')} style={{ width: '100%' }} placeholder="自动计算" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {/* 已投主料信息 */}
            {currentPlan.materials?.filter(m => m.material_type === 1).length ? (
              <>
                <Divider orientation="left">已投主料</Divider>
                <Table size="small" dataSource={currentPlan.materials.filter(m => m.material_type === 1)} rowKey="id" pagination={false}
                  columns={[
                    { title: '商品', render: (_: any, m: any) => m.material?.name || '-' },
                    { title: '批次', dataIndex: 'batch_no' },
                    { title: '投料量', dataIndex: 'actual_qty' },
                    { title: '单价', render: (_: any, m: any) => m.cost_price ? `¥${parseFloat(m.cost_price).toFixed(2)}` : '-' },
                    { title: '小计', render: (_: any, m: any) => `¥${(parseFloat(m.actual_qty) * parseFloat(m.cost_price || 0)).toFixed(2)}` }
                  ]}
                />
              </>
            ) : null}

            {/* 辅料投料 */}
            {auxMaterials.length > 0 && (
              <>
                <Divider orientation="left">辅料投料</Divider>
                <Alert message="系统将按先进先出原则自动从批次扣减库存" type="info" showIcon style={{ marginBottom: 12 }} />
                <Table size="small" dataSource={auxMaterials} rowKey="material_id" pagination={false}
                  columns={[
                    { title: '辅料', dataIndex: 'name', width: 180 },
                    { title: '编码', dataIndex: 'code', width: 120 },
                    {
                      title: '投料量', width: 150,
                      render: (_: any, a: any) => (
                        <InputNumber min={0} value={a.quantity} onChange={v => handleAuxQtyChange(a.material_id, v || 0)} style={{ width: '100%' }} placeholder="输入数量" />
                      )
                    },
                    { title: '单位', dataIndex: 'unit', width: 80 }
                  ]}
                />
              </>
            )}

            <Alert
              message={
                completeOutput > 0
                  ? `成品将以新批次 ${currentPlan.plan_no} 入库，成本按主料+辅料总投入 ÷ 实际产出计算`
                  : '请填入实际产出数量'
              }
              type={completeOutput > 0 ? 'info' : 'warning'}
              showIcon
              style={{ marginTop: 12 }}
            />
          </>
        )}
      </Modal>

      {/* ===== 详情弹窗 ===== */}
      <Modal title="生产单详情" open={detailVisible} onCancel={() => setDetailVisible(false)} footer={<Button onClick={() => setDetailVisible(false)}>关闭</Button>} width={750}>
        {currentPlan && (
          <>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="生产单号">{currentPlan.plan_no}</Descriptions.Item>
              <Descriptions.Item label="状态"><Tag color={statusMap[currentPlan.status]?.color}>{statusMap[currentPlan.status]?.text}</Tag></Descriptions.Item>
              <Descriptions.Item label="成品">{currentPlan.product?.name}</Descriptions.Item>
              <Descriptions.Item label="仓库">{currentPlan.warehouse?.name || '-'}</Descriptions.Item>
              <Descriptions.Item label="实际产出">{currentPlan.actual_output || '-'} {currentPlan.product?.unit || ''}</Descriptions.Item>
              <Descriptions.Item label="损耗率">{currentPlan.waste_rate != null ? `${currentPlan.waste_rate}%` : '-'}</Descriptions.Item>
              <Descriptions.Item label="总成本">{currentPlan.total_cost != null ? `¥${Number(currentPlan.total_cost).toFixed(2)}` : '-'}</Descriptions.Item>
              <Descriptions.Item label="单位成本">{currentPlan.unit_cost != null ? `¥${Number(currentPlan.unit_cost).toFixed(4)}` : '-'}</Descriptions.Item>
              <Descriptions.Item label="开始时间">{currentPlan.started_at || '-'}</Descriptions.Item>
              <Descriptions.Item label="完成时间">{currentPlan.completed_at || '-'}</Descriptions.Item>
              <Descriptions.Item label="备注" span={2}>{currentPlan.remark || '-'}</Descriptions.Item>
            </Descriptions>

            {detailMains.length > 0 && (
              <>
                <Divider orientation="left">主料投料</Divider>
                <Table size="small" dataSource={detailMains} rowKey="id" pagination={false}
                  columns={[
                    { title: '商品', render: (_: any, m: any) => m.material?.name || '-' },
                    { title: '批次', dataIndex: 'batch_no' },
                    { title: '投料量', dataIndex: 'actual_qty' },
                    { title: '单价', render: (_: any, m: any) => m.cost_price ? `¥${parseFloat(m.cost_price).toFixed(4)}` : '-' },
                    { title: '小计', render: (_: any, m: any) => `¥${(parseFloat(m.actual_qty) * parseFloat(m.cost_price || 0)).toFixed(2)}` }
                  ]}
                />
              </>
            )}

            {detailAuxs.length > 0 && (
              <>
                <Divider orientation="left">辅料投料</Divider>
                <Table size="small" dataSource={detailAuxs} rowKey="id" pagination={false}
                  columns={[
                    { title: '辅料', render: (_: any, m: any) => m.material?.name || '-' },
                    { title: '批次', dataIndex: 'batch_no' },
                    { title: '投料量', dataIndex: 'actual_qty' },
                    { title: '单价', render: (_: any, m: any) => m.cost_price ? `¥${parseFloat(m.cost_price).toFixed(4)}` : '-' },
                    { title: '小计', render: (_: any, m: any) => `¥${(parseFloat(m.actual_qty) * parseFloat(m.cost_price || 0)).toFixed(2)}` }
                  ]}
                />
              </>
            )}
          </>
        )}
      </Modal>
    </Card>
  )
}

export default ProductionPage
